/**
 * 클래스 기반 타입 가드
 * @param value unknown
 * @param constructor class
 */
export function isInstanceOf<T>(
  value: unknown,
  constructor: new (...args: never[]) => T,
): value is T {
  return value instanceof constructor;
}

/**
 * JS 기본 Error 또는 확장 Error 여부
 * @param value unknown
 */
export function isError(value: unknown): value is Error {
  if (typeof value !== 'object' || value === null) return false;
  if (!('name' in value) || !('message' in value)) return false;
  const obj = value as { name?: unknown; message?: unknown };
  return typeof obj.name === 'string' && typeof obj.message === 'string';
}

/**
 * 객체(배열 포함) 여부 — 키 존재 확인·프로퍼티 접근 전 좁히기용.
 * @description `typeof null === 'object'` 함정만 제거한 최소 가드. 배열까지 배제해야 하면
 *  `isPlainRecord` 를 쓴다.
 * @param value unknown
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * 배열을 제외한 순수 객체 여부.
 * @description JSON 응답 본문·요청 body 처럼 "키를 가진 맵"을 기대하는 자리에서 배열을 거른다.
 * @param value unknown
 */
export function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && !Array.isArray(value);
}

/**
 * 특정 키를 가진 객체인지 확인 (lightweight 구조 판단용)
 * @param value unknown
 * @param keys T[]
 */
export function hasKeys<T extends string>(value: unknown, keys: T[]): value is Record<T, unknown> {
  return isRecord(value) && keys.every(key => key in value);
}

/**
 * 배열인지 + 배열 내부가 특정 타입인지 확인
 */
export function isArrayOf<T>(arr: unknown, predicate: (item: unknown) => item is T): arr is T[] {
  return Array.isArray(arr) && arr.every(predicate);
}
